import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Lesson, Course } from '@/types';
import { api } from '../utils/apiInterceptor';
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  CheckCircle,
  Circle,
  PlayCircle,
  BookOpen,
  MessageCircle,
  X,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeHighlight from 'rehype-highlight';
import rehypeKatex from 'rehype-katex';
import 'highlight.js/styles/github-dark.css';
import 'katex/dist/katex.min.css';
import './LessonPage.scss';

export const LessonPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const fetchLesson = async () => {
      if (!id) return;

      try {
        const response = await api.get(`/lessons/${id}`);
        const lessonData = response.data.data || response.data;
        setLesson(lessonData);

        if (lessonData.courseId) {
          const courseResponse = await api.get(
            `/courses/${lessonData.courseId}`
          );
          const courseData = courseResponse.data.data || courseResponse.data;
          setCourse(courseData);
        }

        setCompleted(!!lessonData.progress?.completedAt);
      } catch (err) {
        setError('Failed to load lesson');
        console.error('Error fetching lesson:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLesson();
  }, [id]);

  const handleMarkComplete = async () => {
    try {
      const response = await api.post(`/lessons/${id}/complete`);
      console.log('Mark complete response:', response.data);
      setCompleted(true);

      // Update the lesson progress in the sidebar
      if (lesson && course) {
        const updatedLesson = {
          ...lesson,
          progress: {
            ...lesson.progress,
            userId: lesson.progress?.userId || '',
            lessonId: lesson.progress?.lessonId || lesson.id,
            completedAt: new Date().toISOString(),
          },
        };
        setLesson(updatedLesson as any);

        // Update the course lessons array to reflect the completion
        const updatedCourse = {
          ...course,
          lessons: course.lessons?.map(l =>
            l.id === lesson.id ? { ...l, progress: updatedLesson.progress } : l
          ),
        };
        setCourse(updatedCourse as any);
      }
    } catch (error) {
      console.error('Failed to mark lesson as complete:', error);
      throw error; // Re-throw to be caught by the caller
    }
  };

  const getCurrentLessonIndex = () => {
    if (!course?.lessons || !lesson) return 0;
    return course.lessons.findIndex(l => l.id === lesson.id);
  };

  const getNextLesson = () => {
    if (!course?.lessons) {
      console.log('DEBUG: No course lessons available');
      return null;
    }
    const currentIndex = getCurrentLessonIndex();
    console.log('DEBUG: Current lesson index:', currentIndex);
    console.log('DEBUG: Total lessons:', course.lessons.length);
    console.log('DEBUG: Course lessons:', course.lessons.map(l => ({ id: l.id, title: l.title, order: l.order })));
    
    const nextLesson = currentIndex < course.lessons.length - 1
      ? course.lessons[currentIndex + 1]
      : null;
    
    console.log('DEBUG: Next lesson:', nextLesson ? { id: nextLesson.id, title: nextLesson.title } : null);
    return nextLesson;
  };

  const getPreviousLesson = () => {
    if (!course?.lessons) return null;
    const currentIndex = getCurrentLessonIndex();
    return currentIndex > 0 ? course.lessons[currentIndex - 1] : null;
  };

  if (loading) {
    return (
      <div className="lesson-page">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading lesson...</p>
        </div>
      </div>
    );
  }

  if (error || !lesson) {
    return (
      <div className="lesson-page">
        <div className="lesson-container">
          <div className="error-state">
            <h1>Lesson Not Found</h1>
            <p>{error || "The lesson you're looking for doesn't exist."}</p>
            <button
              className="back-button"
              onClick={() => navigate('/dashboard')}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const nextLesson = getNextLesson();
  const previousLesson = getPreviousLesson();
  const currentLessonIndex = getCurrentLessonIndex();

  return (
    <div className="lesson-page">
      <div className="lesson-layout">
        {/* Main Content Area */}
        <div className="lesson-main">
          <div className="lesson-container">
            {/* Lesson Header */}
            <div className="header">
              <div className="header-nav">
                <button
                  className="back-button"
                  onClick={() =>
                    course
                      ? navigate(`/courses/${course.id}`)
                      : navigate('/dashboard')
                  }
                >
                  <ChevronLeft size={16} />
                  Back to Course
                </button>

                <button
                  className="lessons-button"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                >
                  <BookOpen size={18} />
                  <span>Lessons</span>
                </button>
              </div>

              <div className="lesson-header">
                <div className="lesson-meta">
                  {course && <span className="topic">{course.topic}</span>}
                  {course && (
                    <span className={`difficulty ${course.difficulty}`}>
                      {course.difficulty.replace('_', ' ')}
                    </span>
                  )}
                </div>

                <h1>
                  {lesson.title}
                  {completed && (
                    <span className="completion-indicator">
                      <CheckCircle size={20} />
                    </span>
                  )}
                </h1>
                {lesson.description && (
                  <p className="description">{lesson.description}</p>
                )}

                <div className="stats">
                  <div className="stat">
                    <Clock size={16} />
                    {lesson.estimatedMinutes}min
                  </div>
                  <div className="stat">
                    <BookOpen size={16} />
                    Lesson {currentLessonIndex + 1} of{' '}
                    {course?.lessons?.length || 0}
                  </div>
                  {completed && (
                    <div className="stat progress">
                      <CheckCircle size={16} />
                      Complete
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Video Section */}
            {lesson.videoUrl && (
              <div className="video-section">
                <div className="video-header">
                  <h3 className="video-title">Video Lesson</h3>
                  <span className="beta-tag">BETA</span>
                </div>
                <div className="video-wrapper">
                  <iframe
                    src={lesson.videoUrl}
                    title={lesson.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            )}

            {/* Content Section */}
            {(() => {
              let contentToRender = '';

              // Debug logging
              console.log('DEBUG: lesson.content type:', typeof lesson.content);
              console.log('DEBUG: lesson.content value:', lesson.content);

              if (typeof lesson.content === 'string') {
                // If content is a string, try to parse it as JSON first
                try {
                  const parsed = JSON.parse(lesson.content);
                  console.log('DEBUG: parsed content:', parsed);
                  contentToRender =
                    typeof parsed === 'string' ? parsed : lesson.content;
                } catch (e) {
                  console.log('DEBUG: JSON parse failed:', e);
                  // If parsing fails, use it directly (but check if it's just empty quotes)
                  contentToRender =
                    lesson.content === '""' ? '' : lesson.content;
                }
              } else if (
                typeof lesson.content === 'object' &&
                lesson.content !== null
              ) {
                console.log('DEBUG: content is object');
                // If content is an object, check for common properties
                const contentObj = lesson.content as any;
                if (contentObj.markdown) {
                  contentToRender = contentObj.markdown;
                } else if (contentObj.content) {
                  contentToRender = contentObj.content;
                } else {
                  // Try to extract text content or stringify
                  contentToRender = JSON.stringify(lesson.content, null, 2);
                }
              }

              console.log(
                'DEBUG: contentToRender:',
                contentToRender?.substring(0, 100)
              );

              // Show message if no content is available
              if (!contentToRender || contentToRender.trim() === '') {
                return (
                  <div className="content-section">
                    <div className="content-wrapper no-border">
                      <div className="no-content-state">
                        <div className="coming-soon-icon">
                          <Clock size={24} />
                        </div>
                        <h3>Coming Soon</h3>
                        <p>This content is being prepared</p>
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div className="content-section">
                  <div className="content-wrapper">
                    <div className="markdown-content">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm, remarkMath]}
                        rehypePlugins={[rehypeHighlight, rehypeKatex]}
                        components={{
                          h1: ({ children }) => <h1>{children}</h1>,
                          h2: ({ children }) => <h2>{children}</h2>,
                          h3: ({ children }) => <h3>{children}</h3>,
                          h4: ({ children }) => <h4>{children}</h4>,
                          p: ({ children }) => <p>{children}</p>,
                          code: ({
                            inline,
                            className,
                            children,
                            ...props
                          }: any) => {
                            return inline ? (
                              <code {...props}>{children}</code>
                            ) : (
                              <code className={className} {...props}>
                                {children}
                              </code>
                            );
                          },
                          pre: ({ children }) => <pre>{children}</pre>,
                          ul: ({ children }) => (
                            <ul className="enhanced-list">{children}</ul>
                          ),
                          ol: ({ children }) => (
                            <ol className="enhanced-list">{children}</ol>
                          ),
                          li: ({ children }) => (
                            <li className="enhanced-list-item">
                              <div className="list-item-content">
                                {children}
                              </div>
                            </li>
                          ),
                          blockquote: ({ children }) => (
                            <blockquote>{children}</blockquote>
                          ),
                          a: ({ href, children }) => (
                            <a
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {children}
                            </a>
                          ),
                        }}
                      >
                        {contentToRender}
                      </ReactMarkdown>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Quiz Section */}
            {lesson.quiz && (
              <div className="quiz-section">
                <div className="quiz-card">
                  <h3>Knowledge Check</h3>
                  <p>
                    Test your understanding with{' '}
                    {lesson.quiz.questions?.length || 0} questions.
                  </p>
                  <div className="quiz-meta">
                    <div className="quiz-stat">
                      <span className="stat-label">Passing Score</span>
                      <span className="stat-value">
                        {lesson.quiz.passingScore}%
                      </span>
                    </div>
                    {lesson.quiz.timeLimit && (
                      <div className="quiz-stat">
                        <span className="stat-label">Time Limit</span>
                        <span className="stat-value">
                          {lesson.quiz.timeLimit} minutes
                        </span>
                      </div>
                    )}
                  </div>
                  <button
                    className="quiz-button"
                    onClick={() => navigate(`/quizzes/${lesson.quiz!.id}`)}
                  >
                    <PlayCircle size={18} />
                    Start Quiz
                  </button>
                </div>
              </div>
            )}

            {/* Lesson Actions */}
            <div className="lesson-actions">
              <div className="lesson-navigation">
                <div className="nav-section">
                  {previousLesson ? (
                    <button
                      className="nav-button prev"
                      onClick={() => navigate(`/lessons/${previousLesson.id}`)}
                    >
                      <ChevronLeft size={16} />
                      <span>Previous Lesson</span>
                    </button>
                  ) : (
                    <div></div>
                  )}

                  <div className="completion-status">
                    {!completed ? (
                      <button
                        className="mark-complete"
                        onClick={handleMarkComplete}
                      >
                        <CheckCircle size={16} />
                        Mark Complete
                      </button>
                    ) : (
                      <div className="completed-badge">
                        <CheckCircle size={16} />
                        <span>Completed</span>
                      </div>
                    )}
                  </div>

                  {nextLesson ? (
                    <button
                      className="nav-button next"
                      onClick={async () => {
                        try {
                          if (!completed) {
                            await handleMarkComplete();
                          }
                          navigate(`/lessons/${nextLesson.id}`);
                        } catch (error) {
                          console.error('Error navigating to next lesson:', error);
                          // Still navigate even if completion fails
                          navigate(`/lessons/${nextLesson.id}`);
                        }
                      }}
                    >
                      <span>Next Lesson</span>
                      <ChevronRight size={16} />
                    </button>
                  ) : (
                    <button
                      className="nav-button course"
                      onClick={() => navigate(`/courses/${course?.id}`)}
                    >
                      <span>Back to Course</span>
                      <BookOpen size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        {sidebarOpen && (
          <>
            <div
              className="sidebar-overlay"
              onClick={() => setSidebarOpen(false)}
            />
            <div className="lesson-sidebar">
              <div className="sidebar-header">
                <h3>Course Content</h3>
                <button
                  className="close-btn"
                  onClick={() => setSidebarOpen(false)}
                >
                  <X size={20} />
                </button>
              </div>

              <div className="sidebar-content">
                <div className="lessons-list">
                  {course?.lessons?.map((l, index) => {
                    const isActive = l.id === lesson.id;
                    const isCompleted =
                      !!l.progress?.completedAt || (isActive && completed);

                    return (
                      <button
                        key={l.id}
                        className={`lesson-item ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                        onClick={() => {
                          navigate(`/lessons/${l.id}`);
                          setSidebarOpen(false);
                        }}
                      >
                        <div className="lesson-status">
                          {isCompleted ? (
                            <CheckCircle size={18} />
                          ) : isActive ? (
                            <PlayCircle size={18} />
                          ) : (
                            <Circle size={18} />
                          )}
                        </div>
                        <div className="lesson-info">
                          <span className="lesson-number">
                            Lesson {index + 1}
                          </span>
                          <span className="lesson-name">{l.title}</span>
                        </div>
                        <span className="lesson-duration">
                          {l.estimatedMinutes}m
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Floating Action Button for Mobile */}
      <button className="fab" onClick={() => setSidebarOpen(true)}>
        <MessageCircle size={24} />
      </button>
    </div>
  );
};
